import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Target, Info, Calendar, DollarSign, Users, Eye, Download, CreditCard } from 'lucide-react';

const RevenueMetricCard = ({
    title,
    icon: Icon,
    metric,
    formatValue = (v) => v,
    loading = false,
    subtitle,
    showPlanBreakdown = false,
    planData = null,
    showInsights = false,
    monthlyData = [],
    onCardClick = null
}) => {
    const [showDetails, setShowDetails] = useState(false);

    // Enhanced NaN and validation handling
    const isValidMetric = metric && typeof metric === 'object' && !isNaN(metric.current);
    const isPositive = isValidMetric ? metric.change >= 0 : false;
    const progressPercentage = isValidMetric ? Math.min(Math.round((metric.current / metric.goal) * 100), 100) : 0;

    const handleCardClick = () => {
        if (onCardClick) {
            onCardClick();
        } else {
            setShowDetails(true);
        }
    };

    if (loading) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                                <Icon className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                            </div>
                            <span className="text-foreground/90 font-medium">{title}</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            <div className="flex items-center justify-center h-12">
                                <Loader2 className="h-6 w-6 animate-spin text-primary/50" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        );
    }

    // Handle invalid or missing data
    if (!isValidMetric) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
            >
                <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                                <Icon className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                            </div>
                            <span className="text-foreground/90 font-medium">{title}</span>
                        </CardTitle>
                        <div className="text-xs font-medium px-2.5 py-1 rounded-lg bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300">
                            <span className="inline-flex items-center gap-1">
                                <AlertTriangle className="w-3.5 h-3.5" />
                                No Data
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {subtitle && (
                                <p className="text-xs text-muted-foreground/70">{subtitle}</p>
                            )}
                            <div className="flex items-baseline gap-2">
                                <div className="text-2xl font-semibold tracking-tight text-muted-foreground/50">
                                    --
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground/60">
                                Insufficient data for analysis
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        );
    }

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                onClick={handleCardClick}
                className="cursor-pointer"
            >
                <Card className="group relative overflow-hidden bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] border-[hsl(var(--border))] hover:border-[hsl(var(--border))]/40 rounded-xl transition-all duration-200 hover:shadow-md">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-primary/5 group-hover:bg-primary/10 transition-colors">
                                <Icon className="h-4 w-4 text-primary/50 group-hover:text-primary/80 transition-colors" />
                            </div>
                            <span className="text-foreground/90 font-medium">{title}</span>
                        </CardTitle>
                        <div className={`text-xs font-medium px-2.5 py-1 rounded-lg transition-colors ${isPositive
                            ? 'text-[hsl(142.1_76.2%_36.3%)] dark:text-[hsl(142.1_76.2%_45.3%)] bg-[hsl(142.1_76.2%_36.3%)]/5 group-hover:bg-[hsl(142.1_76.2%_36.3%)]/10'
                            : 'text-[hsl(0_84.2%_60.2%)] dark:text-[hsl(0_84.2%_70.2%)] bg-[hsl(0_84.2%_60.2%)]/5 group-hover:bg-[hsl(0_84.2%_60.2%)]/10'
                            }`}>
                            <span className={`inline-flex items-center gap-1 ${isPositive
                                ? 'text-[hsl(142.1_76.2%_36.3%)] dark:text-[hsl(142.1_76.2%_45.3%)]'
                                : 'text-[hsl(0_84.2%_60.2%)] dark:text-[hsl(0_84.2%_70.2%)]'
                                }`}>
                                {isPositive ? (
                                    <TrendingUp className="w-3.5 h-3.5" />
                                ) : (
                                    <TrendingDown className="w-3.5 h-3.5" />
                                )}
                                {Math.round(Math.abs(metric.change))}%
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {subtitle && (
                                <p className="text-xs text-muted-foreground/70">{subtitle}</p>
                            )}

                            <div className="text-2xl font-semibold tracking-tight text-foreground/95 dark:text-foreground/90">
                                {formatValue(metric.current)}
                            </div>

                            {/* Simple goal indicator */}
                            {metric.goal && (
                                <div className="text-xs text-muted-foreground/70">
                                    Goal: {formatValue(metric.goal)}
                                </div>
                            )}

                            {/* Simple progress indicator */}
                            {metric.goal && (
                                <div className="text-xs text-muted-foreground/70">
                                    {Math.round(progressPercentage)}% of goal
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </motion.div>

            {/* Detailed Modal */}
            <Dialog open={showDetails} onOpenChange={setShowDetails}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader className="pb-4 border-b">
                        <DialogTitle className="flex items-center gap-3 text-xl">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Icon className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <div className="font-bold">{title}</div>
                                <div className="text-sm font-normal text-muted-foreground">{subtitle}</div>
                            </div>
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 pt-4">
                        {/* Key Metrics Overview */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border border-green-200 dark:border-green-800">
                                <div className="flex items-center gap-2 mb-2">
                                    <DollarSign className="h-4 w-4 text-green-600" />
                                    <span className="text-sm font-medium text-green-700 dark:text-green-300">Current Value</span>
                                </div>
                                <div className="text-2xl font-bold text-green-800 dark:text-green-200">
                                    {formatValue(metric.current)}
                                </div>
                            </div>

                            <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border border-blue-200 dark:border-blue-800">
                                <div className="flex items-center gap-2 mb-2">
                                    <Target className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Goal</span>
                                </div>
                                <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
                                    {formatValue(metric.goal)}
                                </div>
                            </div>

                            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border border-purple-200 dark:border-purple-800">
                                <div className="flex items-center gap-2 mb-2">
                                    <TrendingUp className="h-4 w-4 text-purple-600" />
                                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Change</span>
                                </div>
                                <div className={`text-2xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                    {isPositive ? '+' : ''}{Math.round(metric.change)}%
                                </div>
                            </div>
                        </div>

                        {/* Progress Section */}
                        {metric.goal && (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold">Progress Toward Goal</h3>
                                    <Badge variant={progressPercentage >= 80 ? "default" : progressPercentage >= 50 ? "secondary" : "destructive"}>
                                        {Math.round(progressPercentage)}% Complete
                                    </Badge>
                                </div>
                                <div className="space-y-2">
                                    <Progress value={progressPercentage} className="h-4" />
                                    <div className="flex justify-between text-sm text-muted-foreground">
                                        <span>{Math.round(metric.current)} achieved</span>
                                        <span>{Math.round(metric.goal)} target</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Detailed Analysis */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Change Analysis */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <TrendingUp className="h-5 w-5" />
                                    Change Analysis
                                </h3>
                                <div className="space-y-3">
                                    <div className="p-4 rounded-lg bg-muted/30 border">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium">Previous Period</span>
                                            <Calendar className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <div className="text-xl font-bold">{formatValue(metric.previous || 0)}</div>
                                    </div>

                                    <div className="p-4 rounded-lg bg-muted/30 border">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium">Period Change</span>
                                            <div className={`p-1 rounded ${isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                            </div>
                                        </div>
                                        <div className={`text-xl font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                                            {isPositive ? '+' : ''}{Math.round(metric.change)}%
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Performance Indicators */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <Target className="h-5 w-5" />
                                    Performance Status
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border">
                                        <div className="flex items-center gap-2">
                                            <Target className="h-4 w-4 text-blue-500" />
                                            <span className="text-sm font-medium">On Track</span>
                                        </div>
                                        <Badge variant={metric.current >= (metric.goal * 0.8) ? "default" : "destructive"}>
                                            {metric.current >= (metric.goal * 0.8) ? 'Yes' : 'No'}
                                        </Badge>
                                    </div>

                                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border">
                                        <div className="flex items-center gap-2">
                                            <TrendingUp className="h-4 w-4 text-green-500" />
                                            <span className="text-sm font-medium">Trend Direction</span>
                                        </div>
                                        <Badge variant={isPositive ? "default" : "destructive"}>
                                            {isPositive ? 'Growing' : 'Declining'}
                                        </Badge>
                                    </div>

                                    <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border">
                                        <div className="flex items-center gap-2">
                                            <Info className="h-4 w-4 text-purple-500" />
                                            <span className="text-sm font-medium">Confidence</span>
                                        </div>
                                        <Badge variant="outline">
                                            {Math.abs(metric.change) > 20 ? 'High' : Math.abs(metric.change) > 10 ? 'Medium' : 'Low'}
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Recent Trends */}
                        {monthlyData && monthlyData.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <Calendar className="h-5 w-5" />
                                    Recent Trends (Last 5 Periods)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {monthlyData.slice(-5).map((item, index) => (
                                        <div key={index} className="p-3 rounded-lg bg-muted/30 border hover:bg-muted/50 transition-colors">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-3 w-3 text-muted-foreground" />
                                                    <span className="text-sm font-medium">{item.name}</span>
                                                </div>
                                            </div>
                                            <div className="text-lg font-bold">
                                                {title.includes('Revenue') ? `$${Math.round(item.revenue || 0)}` :
                                                    title.includes('Subscribers') ? Math.round(item.subscriptions || 0) :
                                                        title.includes('Views') ? Math.round(item.downloads * 2.5 || 0) :
                                                            Math.round(item.downloads || 0)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Insights & Recommendations */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <Info className="h-5 w-5" />
                                Insights & Recommendations
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="h-4 w-4 text-blue-600" />
                                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">What's Working</span>
                                    </div>
                                    <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-1">
                                        {isPositive && <li>• Growth trend is positive</li>}
                                        {progressPercentage > 50 && <li>• More than halfway to goal</li>}
                                        {metric.current > 0 && <li>• Consistent activity detected</li>}
                                        {!isPositive && progressPercentage < 30 && <li>• Room for significant improvement</li>}
                                    </ul>
                                </div>

                                <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
                                    <div className="flex items-center gap-2 mb-2">
                                        <AlertTriangle className="h-4 w-4 text-orange-600" />
                                        <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Areas for Improvement</span>
                                    </div>
                                    <ul className="text-sm text-orange-600 dark:text-orange-300 space-y-1">
                                        {progressPercentage < 50 && <li>• Focus on goal achievement</li>}
                                        {!isPositive && <li>• Address declining trends</li>}
                                        {metric.current === 0 && <li>• Start generating activity</li>}
                                        {progressPercentage > 80 && <li>• Consider increasing goals</li>}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default RevenueMetricCard; 